import { ReactNode, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "../../../../utils/styles";
import { CountryCombobox } from "./CountryCombobox";
import { FieldGrid } from "./styles";

// Who's paying, only what Stripe needs: country and name for everyone; CPF/CNPJ for Pix; CPF/CNPJ and the full
// address for boleto. Keepo issues a single tax invoice, not one per buyer, so nothing else is asked

export type BillingDetailsValue = {
  country: string;
  name: string;
  /** CPF or CNPJ (Brazil: Pix and boleto). */
  document: string;
  /** CEP and the rest of the address (Brazil: boleto). */
  postalCode: string;
  line1: string;
  number: string;
  line2: string;
  /** Bairro. */
  district: string;
  city: string;
  /** UF. */
  state: string;
};

export type BillingErrors = Partial<Record<keyof BillingDetailsValue, string>>;

export const EMPTY_BILLING: BillingDetailsValue = {
  country: "BR",
  name: "",
  document: "",
  postalCode: "",
  line1: "",
  number: "",
  line2: "",
  district: "",
  city: "",
  state: "",
};

/**
 * Starts the form with the billing details the user last used (saved on the account, so any of their Keepos):
 * only the fields still untouched; the account's name when none was saved.
 */
export const withSavedBilling = (
  current: BillingDetailsValue,
  saved: Partial<Record<keyof BillingDetailsValue, string>> | null | undefined,
  accountName?: string
): BillingDetailsValue => {
  const next = { ...current };
  (Object.keys(EMPTY_BILLING) as (keyof BillingDetailsValue)[]).forEach(field => {
    const value = saved?.[field];
    if (value && current[field] === EMPTY_BILLING[field]) {
      next[field] = field === "document" ? formatDocument(value) : field === "postalCode" ? formatCep(value) : value;
    }
  });
  if (!next.name && accountName) {
    next.name = accountName;
  }
  return next;
};

/** What ViaCEP answers for a CEP (`erro` when it doesn't exist). */
type ViaCepAddress = {
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean | string;
};

/** What the chosen payment needs beyond country and name. */
export type BillingNeeds = "none" | "document" | "address";

// ISO 3166-1 alpha-2; the names come from the browser in the user's language
const COUNTRY_CODES = (
  "AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ CA CD CF CG CH " +
  "CI CL CM CN CO CR CV CY CZ DE DJ DK DM DO DZ EC EE EG ES ET FI FJ FM FR GA GB GD GE GH GI GM GN GQ GR GT GW " +
  "GY HK HN HR HT HU ID IE IL IN IQ IS IT JM JO JP KE KG KH KI KM KN KR KW KY KZ LA LB LC LI LK LR LS LT LU LV " +
  "LY MA MC MD ME MG MH MK ML MM MN MO MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL " +
  "PR PS PT PW PY QA RO RS RW SA SB SC SE SG SI SK SL SM SN SO SR ST SV SZ TC TD TG TH TJ TL TM TN TO TR TT TV " +
  "TW TZ UA UG US UY UZ VC VE VG VN VU WS XK YE ZA ZM ZW"
).split(" ");

const BR_STATES = "AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO".split(" ");

const digitsOf = (value: string) => value.replace(/\D/g, "");

export const formatDocument = (value: string) => {
  const digits = digitsOf(value).slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const formatCep = (value: string) =>
  digitsOf(value)
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, "$1-$2");

const isValidCpf = (cpf: string) => {
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  const check = (length: number) => {
    const sum = [...cpf.slice(0, length)].reduce(
      (total, digit, index) => total + Number(digit) * (length + 1 - index),
      0
    );
    return ((sum * 10) % 11) % 10;
  };
  return check(9) === Number(cpf[9]) && check(10) === Number(cpf[10]);
};

const isValidCnpj = (cnpj: string) => {
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const check = (length: number) => {
    const weights = length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((total, weight, index) => total + Number(cnpj[index]) * weight, 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return check(12) === Number(cnpj[12]) && check(13) === Number(cnpj[13]);
};

/** What's wrong, as keys to translate under `checkout.billing.errors`. A CPF/CNPJ typed when not needed must
 * still be valid. */
export const validateBilling = (value: BillingDetailsValue, needs: BillingNeeds = "none"): BillingErrors => {
  const errors: BillingErrors = {};
  if (value.name.trim().split(/\s+/).length < 2) errors.name = "name";
  if (value.country !== "BR") {
    return errors;
  }

  const document = digitsOf(value.document);
  if ((needs !== "none" || document) && !(isValidCpf(document) || isValidCnpj(document))) {
    errors.document = "document";
  }
  if (needs === "address") {
    if (digitsOf(value.postalCode).length !== 8) errors.postalCode = "postalCode";
    if (!value.line1.trim()) errors.line1 = "required";
    if (!value.number.trim()) errors.number = "required";
    if (!value.district.trim()) errors.district = "required";
    if (!value.city.trim()) errors.city = "required";
    if (!value.state) errors.state = "required";
  }
  return errors;
};

type FieldProps = {
  label: string;
  error?: string;
  hint?: ReactNode;
  full?: boolean;
  /** The whole line on mobile only (two per line on desktop). */
  fullOnMobile?: boolean;
  children: ReactNode;
};

export const Field = ({ label, error, hint, full, fullOnMobile, children }: FieldProps) => (
  <label className={classNames("field", { full: !!full, "full-mobile": !!fullOnMobile, invalid: !!error })}>
    <span className="label">{label}</span>
    <span className="control">{children}</span>
    {error ? <small>{error}</small> : hint && <small className="hint">{hint}</small>}
  </label>
);

type Props = {
  value: BillingDetailsValue;
  onChange: (value: BillingDetailsValue) => void;
  errors: BillingErrors;
};

const useErrorOf = (errors: BillingErrors) => {
  const { t } = useTranslation();
  return (field: keyof BillingDetailsValue) =>
    errors[field] ? t(`pages.subscription.checkout.billing.errors.${errors[field]}`) : undefined;
};

/**
 * Country and name; `withBrazilFields` adds CPF/CNPJ (beside the name on desktop) and address in Brazil (editing
 * the billing details). `afterDocument`: more fields (e.g. e-mail and phone) right under the name and CPF, before
 * the address.
 */
export const BillingDetails = ({
  value,
  onChange,
  errors,
  withBrazilFields,
  afterDocument,
}: Props & { withBrazilFields?: boolean; afterDocument?: ReactNode }) => {
  const { t, i18n } = useTranslation();
  const prefix = "pages.subscription.checkout.billing";
  const errorOf = useErrorOf(errors);

  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([i18n.language], { type: "region" });
    return COUNTRY_CODES.map(code => ({ code, name: names.of(code) || code })).sort((a, b) =>
      a.name.localeCompare(b.name, i18n.language)
    );
  }, [i18n.language]);

  const withDocument = withBrazilFields && value.country === "BR";

  return (
    <FieldGrid>
      <Field label={t(`${prefix}.country`)} full>
        <CountryCombobox
          value={value.country}
          countries={countries}
          // The Brazilian fields don't apply elsewhere: they don't carry over
          onChange={country => onChange({ ...EMPTY_BILLING, name: value.name, country })}
        />
      </Field>

      <Field label={t(`${prefix}.name`)} error={errorOf("name")} full={!withDocument} fullOnMobile={withDocument}>
        <input
          autoComplete="name"
          placeholder={t(`${prefix}.namePlaceholder`)}
          value={value.name}
          onChange={e => onChange({ ...value, name: e.target.value })}
        />
      </Field>

      {withDocument && <BrazilDocument value={value} onChange={onChange} errors={errors} beside />}
      {afterDocument}
      {withDocument && <BrazilAddress value={value} onChange={onChange} errors={errors} />}
    </FieldGrid>
  );
};

/** CPF or CNPJ (Pix and boleto). `beside`: half the line on desktop (next to the name), whole on mobile. */
export const BrazilDocument = ({ value, onChange, errors, beside }: Props & { beside?: boolean }) => {
  const { t } = useTranslation();
  const errorOf = useErrorOf(errors);
  return (
    <Field
      label={t("pages.subscription.checkout.billing.document")}
      error={errorOf("document")}
      full={!beside}
      fullOnMobile={beside}>
      <input
        inputMode="numeric"
        placeholder="000.000.000-00"
        value={value.document}
        onChange={e => onChange({ ...value, document: formatDocument(e.target.value) })}
      />
    </Field>
  );
};

// Where to find a CEP from the address, for whoever doesn't know theirs
const CORREIOS_CEP_SEARCH = "https://buscacepinter.correios.com.br/app/endereco/index.php";

/**
 * CEP, street, number and the rest of a Brazilian address (boleto). At first only the CEP: the rest opens
 * once it's looked up, filled with what ViaCEP knows (empty, to be typed, when it doesn't know it or is down).
 */
export const BrazilAddress = ({ value, onChange, errors }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.subscription.checkout.billing";
  const set = (patch: Partial<BillingDetailsValue>) => onChange({ ...value, ...patch });
  const errorOf = useErrorOf(errors);

  // A full CEP fills the rest of the address from ViaCEP; what comes back lands on the latest value
  // (the person may keep typing meanwhile), then the number is next
  const [cepLookup, setCepLookup] = useState<"idle" | "loading" | "notFound">("idle");
  const latest = useRef(value);
  latest.current = value;
  const lookup = useRef<AbortController>();
  const numberRef = useRef<HTMLInputElement>(null);
  // Open for good once looked up (or already filled, e.g. back from the payment screen): erasing the CEP
  // afterwards doesn't hide what's being typed
  const [isOpened, setOpen] = useState(false);
  const isOpen = isOpened || !!(value.line1 || value.number || value.district || value.city || value.state);

  const changeCep = (typed: string) => {
    const postalCode = formatCep(typed);
    set({ postalCode });
    lookup.current?.abort();
    const cep = digitsOf(postalCode);
    if (cep.length !== 8) {
      setCepLookup("idle");
      return;
    }
    const controller = new AbortController();
    lookup.current = controller;
    setCepLookup("loading");
    fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal: controller.signal })
      .then(response => (response.ok ? response.json() : Promise.reject(response)))
      .then((address: ViaCepAddress) => {
        setOpen(true);
        if (address.erro) {
          setCepLookup("notFound");
          return;
        }
        setCepLookup("idle");
        const current = latest.current;
        onChange({
          ...current,
          line1: address.logradouro || current.line1,
          district: address.bairro || current.district,
          city: address.localidade || current.city,
          state: address.uf || current.state,
        });
        // Once the fields are on screen
        requestAnimationFrame(() => numberRef.current?.focus());
      })
      // Offline or ViaCEP down: the address is typed by hand
      .catch(error => {
        if (error?.name !== "AbortError") {
          setCepLookup("idle");
          setOpen(true);
        }
      });
  };

  return (
    <>
      <Field
        label={t(`${prefix}.cep`)}
        fullOnMobile
        error={cepLookup === "notFound" ? t(`${prefix}.errors.cepNotFound`) : errorOf("postalCode")}
        hint={cepLookup === "loading" ? t(`${prefix}.cepLoading`) : undefined}>
        <input
          inputMode="numeric"
          autoComplete="postal-code"
          placeholder="00000-000"
          value={value.postalCode}
          onChange={e => changeCep(e.target.value)}
        />
        {/* Inside the field, on the right, until something is typed */}
        {!value.postalCode && (
          <a className="inline-link" href={CORREIOS_CEP_SEARCH} target="_blank" rel="noreferrer">
            {t(`${prefix}.cepUnknown`)}
          </a>
        )}
      </Field>
      {isOpen && (
        <>
          <Field label={t(`${prefix}.uf`)} error={errorOf("state")} fullOnMobile>
            <select autoComplete="address-level1" value={value.state} onChange={e => set({ state: e.target.value })}>
              <option value="" disabled>
                —
              </option>
              {BR_STATES.map(uf => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t(`${prefix}.street`)} error={errorOf("line1")} full>
            <input autoComplete="address-line1" value={value.line1} onChange={e => set({ line1: e.target.value })} />
          </Field>
          <Field label={t(`${prefix}.number`)} error={errorOf("number")}>
            <input ref={numberRef} value={value.number} onChange={e => set({ number: e.target.value })} />
          </Field>
          <Field label={t(`${prefix}.complement`)}>
            <input autoComplete="address-line2" value={value.line2} onChange={e => set({ line2: e.target.value })} />
          </Field>
          <Field label={t(`${prefix}.district`)} error={errorOf("district")}>
            <input value={value.district} onChange={e => set({ district: e.target.value })} />
          </Field>
          <Field label={t(`${prefix}.city`)} error={errorOf("city")}>
            <input autoComplete="address-level2" value={value.city} onChange={e => set({ city: e.target.value })} />
          </Field>
        </>
      )}
    </>
  );
};
